/**
 * Minimal QR Code encoder for TOTP enrollment
 *
 * Supports byte mode, error correction level M, versions 1-10.
 * No external dependencies — implements the QR code spec directly.
 */

// =============================================================================
// GF(256) Arithmetic (for Reed-Solomon error correction)
// =============================================================================

// Primitive polynomial: x^8 + x^4 + x^3 + x^2 + 1 (0x11d)
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x = (x << 1) ^ (x >= 128 ? 0x11d : 0);
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
}

function gfMul(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

// =============================================================================
// Reed-Solomon Encoding
// =============================================================================

function rsGenPoly(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: Uint8Array, ecCount: number): Uint8Array {
  const gen = rsGenPoly(ecCount);
  const result = new Uint8Array(ecCount);
  for (let i = 0; i < data.length; i++) {
    const coeff = data[i] ^ result[0];
    for (let j = 0; j < ecCount - 1; j++) result[j] = result[j + 1];
    result[ecCount - 1] = 0;
    if (coeff !== 0) {
      for (let j = 0; j < ecCount; j++) result[j] ^= gfMul(gen[j + 1], coeff);
    }
  }
  return result;
}

// =============================================================================
// QR Code Tables (EC level M, versions 1-10)
// =============================================================================

// [ecPerBlock, group1Blocks, group1DataCw, group2Blocks, group2DataCw]
const EC_M: number[][] = [
  [],
  [10, 1, 16, 0, 0],    // v1
  [16, 1, 28, 0, 0],    // v2
  [26, 1, 44, 0, 0],    // v3
  [18, 2, 32, 0, 0],    // v4
  [24, 2, 43, 0, 0],    // v5
  [16, 4, 27, 0, 0],    // v6
  [18, 4, 31, 0, 0],    // v7
  [22, 2, 38, 2, 39],   // v8
  [22, 3, 36, 2, 37],   // v9
  [26, 4, 43, 1, 44],   // v10
];

// Max data bytes for byte mode, EC level M
const CAPACITY = [0, 14, 26, 42, 62, 84, 106, 122, 152, 180, 213];

// Alignment pattern center positions
const ALIGN_POS: number[][] = [
  [],
  [], [6, 18], [6, 22], [6, 26], [6, 30],
  [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

// =============================================================================
// Data Encoding
// =============================================================================

function encodeData(text: string, version: number): Uint8Array {
  const bytes = new TextEncoder().encode(text);
  const params = EC_M[version];
  const totalDataCw = params[1] * params[2] + params[3] * params[4];
  const maxBits = totalDataCw * 8;
  const bits: number[] = [];

  // Mode indicator: byte mode (0100)
  bits.push(0, 1, 0, 0);

  // Character count (8 bits for versions 1-9, 16 for 10+)
  const countBits = version <= 9 ? 8 : 16;
  for (let i = countBits - 1; i >= 0; i--) bits.push((bytes.length >> i) & 1);

  // Data bytes
  for (const b of bytes) for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);

  // Terminator (up to 4 zeros)
  for (let i = 0; i < 4 && bits.length < maxBits; i++) bits.push(0);

  // Pad to byte boundary
  while (bits.length % 8 !== 0 && bits.length < maxBits) bits.push(0);

  // Pad with alternating 0xEC, 0x11
  let padIdx = 0;
  while (bits.length < maxBits) {
    const pb = padIdx % 2 === 0 ? 0xEC : 0x11;
    for (let i = 7; i >= 0; i--) bits.push((pb >> i) & 1);
    padIdx++;
  }

  const codewords = new Uint8Array(totalDataCw);
  for (let i = 0; i < totalDataCw; i++) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | (bits[i * 8 + j] || 0);
    codewords[i] = byte;
  }
  return codewords;
}

// =============================================================================
// Error Correction & Interleaving
// =============================================================================

function computeCodewords(dataCw: Uint8Array, version: number): Uint8Array {
  const [ecPerBlock, g1Blocks, g1DataCw, g2Blocks, g2DataCw] = EC_M[version];
  const blocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;

  for (let i = 0; i < g1Blocks; i++) {
    const block = dataCw.slice(offset, offset + g1DataCw);
    blocks.push(block);
    ecBlocks.push(rsEncode(block, ecPerBlock));
    offset += g1DataCw;
  }
  for (let i = 0; i < g2Blocks; i++) {
    const block = dataCw.slice(offset, offset + g2DataCw);
    blocks.push(block);
    ecBlocks.push(rsEncode(block, ecPerBlock));
    offset += g2DataCw;
  }

  // Interleave data codewords
  const totalBlocks = g1Blocks + g2Blocks;
  const maxDataCw = Math.max(g1DataCw, g2DataCw);
  const result: number[] = [];

  for (let i = 0; i < maxDataCw; i++) {
    for (let b = 0; b < totalBlocks; b++) {
      if (i < blocks[b].length) result.push(blocks[b][i]);
    }
  }

  // Interleave EC codewords
  for (let i = 0; i < ecPerBlock; i++) {
    for (let b = 0; b < totalBlocks; b++) result.push(ecBlocks[b][i]);
  }

  return new Uint8Array(result);
}

// =============================================================================
// Matrix Construction
// =============================================================================

function placeFinder(
  mod: boolean[][], fn: boolean[][],
  row: number, col: number
): void {
  const size = mod.length;
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r, cc = col + c;
      if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
      const isSep = r === -1 || r === 7 || c === -1 || c === 7;
      const border = r === 0 || r === 6 || c === 0 || c === 6;
      const center = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      mod[rr][cc] = !isSep && (border || center);
      fn[rr][cc] = true;
    }
  }
}

function placeAlignment(
  mod: boolean[][], fn: boolean[][],
  row: number, col: number
): void {
  for (let r = -2; r <= 2; r++) {
    for (let c = -2; c <= 2; c++) {
      mod[row + r][col + c] = Math.abs(r) === 2 || Math.abs(c) === 2 || (r === 0 && c === 0);
      fn[row + r][col + c] = true;
    }
  }
}

function createMatrix(version: number): { mod: boolean[][]; fn: boolean[][] } {
  const size = version * 4 + 17;
  const mod = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const fn = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

  // Finder patterns
  placeFinder(mod, fn, 0, 0);
  placeFinder(mod, fn, size - 7, 0);
  placeFinder(mod, fn, 0, size - 7);

  // Alignment patterns
  const positions = ALIGN_POS[version];
  if (positions.length > 0) {
    for (const r of positions) {
      for (const c of positions) {
        if (fn[r][c]) continue; // Skip if overlapping finder
        placeAlignment(mod, fn, r, c);
      }
    }
  }

  // Timing patterns
  for (let i = 8; i < size - 8; i++) {
    if (!fn[6][i]) { mod[6][i] = i % 2 === 0; fn[6][i] = true; }
    if (!fn[i][6]) { mod[i][6] = i % 2 === 0; fn[i][6] = true; }
  }

  // Dark module
  mod[size - 8][8] = true;
  fn[size - 8][8] = true;

  // Reserve format info areas
  for (let i = 0; i < 9; i++) {
    if (!fn[8][i]) fn[8][i] = true;
    if (!fn[i][8]) fn[i][8] = true;
  }
  for (let i = 0; i < 8; i++) {
    fn[8][size - 8 + i] = true;
  }
  for (let i = 0; i < 7; i++) {
    fn[size - 7 + i][8] = true;
  }

  // Reserve version info areas (version >= 7)
  if (version >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        fn[i][size - 11 + j] = true;
        fn[size - 11 + j][i] = true;
      }
    }
  }

  return { mod, fn };
}

// =============================================================================
// Data Placement (zigzag pattern)
// =============================================================================

function placeData(mod: boolean[][], fn: boolean[][], codewords: Uint8Array): void {
  const size = mod.length;
  let bitIdx = 0;
  const totalBits = codewords.length * 8;
  let strip = 0;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // Skip timing column
    const goingUp = strip % 2 === 0;

    for (let vert = 0; vert < size; vert++) {
      const row = goingUp ? size - 1 - vert : vert;
      for (const dc of [0, 1]) {
        const col = right - dc;
        if (col < 0 || fn[row][col]) continue;
        if (bitIdx < totalBits) {
          mod[row][col] = ((codewords[bitIdx >> 3] >> (7 - (bitIdx & 7))) & 1) === 1;
          bitIdx++;
        }
      }
    }
    strip++;
  }
}

// =============================================================================
// Masking
// =============================================================================

type MaskFn = (r: number, c: number) => boolean;

const MASKS: MaskFn[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function applyMask(mod: boolean[][], fn: boolean[][], mask: number): void {
  const size = mod.length;
  const test = MASKS[mask];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!fn[r][c]) mod[r][c] = mod[r][c] !== test(r, c);
    }
  }
}

// =============================================================================
// Penalty Score
// =============================================================================

function penalty(mod: boolean[][]): number {
  const size = mod.length;
  let score = 0;

  // Rule 1: Runs of same color (rows and columns)
  for (let r = 0; r < size; r++) {
    let count = 1;
    for (let c = 1; c < size; c++) {
      if (mod[r][c] === mod[r][c - 1]) { count++; }
      else { if (count >= 5) score += count - 2; count = 1; }
    }
    if (count >= 5) score += count - 2;
  }
  for (let c = 0; c < size; c++) {
    let count = 1;
    for (let r = 1; r < size; r++) {
      if (mod[r][c] === mod[r - 1][c]) { count++; }
      else { if (count >= 5) score += count - 2; count = 1; }
    }
    if (count >= 5) score += count - 2;
  }

  // Rule 2: 2x2 blocks of same color
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = mod[r][c];
      if (v === mod[r][c + 1] && v === mod[r + 1][c] && v === mod[r + 1][c + 1]) score += 3;
    }
  }

  // Rule 3: Finder-like patterns
  const pat1 = [true, false, true, true, true, false, true, false, false, false, false];
  const pat2 = [false, false, false, false, true, false, true, true, true, false, true];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c <= size - 11; c++) {
      if (pat1.every((v, i) => mod[r][c + i] === v)) score += 40;
      if (pat2.every((v, i) => mod[r][c + i] === v)) score += 40;
    }
  }
  for (let c = 0; c < size; c++) {
    for (let r = 0; r <= size - 11; r++) {
      if (pat1.every((v, i) => mod[r + i][c] === v)) score += 40;
      if (pat2.every((v, i) => mod[r + i][c] === v)) score += 40;
    }
  }

  // Rule 4: Dark module proportion
  let dark = 0;
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (mod[r][c]) dark++;
  const pct = (dark * 100) / (size * size);
  const prev5 = Math.floor(pct / 5) * 5;
  score += Math.min(Math.abs(prev5 - 50), Math.abs(prev5 + 5 - 50)) * 2;

  return score;
}

// =============================================================================
// Format & Version Information
// =============================================================================

function formatInfo(mask: number): number {
  // EC level M = 0b00
  let data = mask; // 00 xxx → just the mask bits since EC level M = 0
  let rem = data << 10;
  for (let i = 4; i >= 0; i--) {
    if (rem & (1 << (i + 10))) rem ^= 0x537 << i;
  }
  return ((data << 10) | rem) ^ 0x5412;
}

function placeFormatInfo(mod: boolean[][], size: number, mask: number): void {
  const bits = formatInfo(mask);

  // Copy 1: around top-left finder
  // Row 8: bits 0-7 at columns 0,1,2,3,4,5,7,8 (skip col 6 = timing)
  const c1Cols = [0, 1, 2, 3, 4, 5, 7, 8];
  // Column 8: bits 8-14 at rows 7,5,4,3,2,1,0 (skip row 6 = timing)
  const c1Rows = [7, 5, 4, 3, 2, 1, 0];

  for (let i = 0; i < 8; i++) {
    mod[8][c1Cols[i]] = ((bits >> i) & 1) === 1;
  }
  for (let i = 0; i < 7; i++) {
    mod[c1Rows[i]][8] = ((bits >> (i + 8)) & 1) === 1;
  }

  // Copy 2: around other two finders
  for (let i = 0; i < 7; i++) {
    mod[size - 1 - i][8] = ((bits >> i) & 1) === 1;
  }
  for (let i = 0; i < 8; i++) {
    mod[8][size - 8 + i] = ((bits >> (i + 7)) & 1) === 1;
  }
}

function placeVersionInfo(mod: boolean[][], version: number): void {
  if (version < 7) return;
  const size = mod.length;
  let rem = version << 12;
  for (let i = 5; i >= 0; i--) {
    if (rem & (1 << (i + 12))) rem ^= 0x1F25 << i;
  }
  const bits = (version << 12) | rem;

  for (let i = 0; i < 18; i++) {
    const bit = ((bits >> i) & 1) === 1;
    const r = Math.floor(i / 3), c = i % 3;
    mod[r][size - 11 + c] = bit;       // top-right
    mod[size - 11 + c][r] = bit;       // bottom-left
  }
}

// =============================================================================
// Deep Copy Helper
// =============================================================================

function copyMatrix(src: boolean[][]): boolean[][] {
  return src.map(row => row.slice());
}

// =============================================================================
// Main QR Generation
// =============================================================================

function generateQR(text: string): boolean[][] | null {
  // Select minimum version
  const bytes = new TextEncoder().encode(text);
  let version = 0;
  for (let v = 1; v <= 10; v++) {
    if (bytes.length <= CAPACITY[v]) { version = v; break; }
  }
  if (version === 0) return null; // Data too long

  const size = version * 4 + 17;

  // Encode data
  const dataCw = encodeData(text, version);
  const codewords = computeCodewords(dataCw, version);

  // Build matrix with data placed
  const { mod: baseMod, fn } = createMatrix(version);
  placeData(baseMod, fn, codewords);

  // Try all 8 masks, pick the one with lowest penalty
  let bestMask = 0;
  let bestScore = Infinity;
  let bestMod: boolean[][] = baseMod;

  for (let mask = 0; mask < 8; mask++) {
    const trial = copyMatrix(baseMod);
    applyMask(trial, fn, mask);
    placeFormatInfo(trial, size, mask);
    placeVersionInfo(trial, version);

    const score = penalty(trial);
    if (score < bestScore) {
      bestScore = score;
      bestMask = mask;
      bestMod = trial;
    }
  }

  // Apply best mask to the actual matrix
  if (bestMod === baseMod) {
    applyMask(baseMod, fn, bestMask);
    placeFormatInfo(baseMod, size, bestMask);
    placeVersionInfo(baseMod, version);
    return baseMod;
  }

  return bestMod;
}

// =============================================================================
// Public API: Render to Canvas
// =============================================================================

export function renderQrCode(canvas: HTMLCanvasElement, data: string, size = 200): void {
  const modules = generateQR(data);
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  if (!modules) {
    ctx.fillStyle = '#f44';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Data too long for QR', size / 2, size / 2);
    return;
  }

  const qrSize = modules.length;
  const quietZone = 4;
  const total = qrSize + quietZone * 2;
  const modSize = size / total;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);

  ctx.fillStyle = '#000000';
  for (let r = 0; r < qrSize; r++) {
    const y = Math.round((r + quietZone) * modSize);
    const yNext = Math.round((r + quietZone + 1) * modSize);
    for (let c = 0; c < qrSize; c++) {
      if (modules[r][c]) {
        const x = Math.round((c + quietZone) * modSize);
        const xNext = Math.round((c + quietZone + 1) * modSize);
        ctx.fillRect(x, y, xNext - x, yNext - y);
      }
    }
  }
}
